import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Trash2, Save, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOutletContext } from 'react-router-dom';
import { getAdminConfig, updateAdminConfig } from '../../utils/api';
import type { AdminConfig, SynonymGroup } from '../../utils/types';
import { useAdminLang } from '../AdminLangContext';

interface AdminContext {
  secret: string;
}

export default function AdminSearchConfig() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    getAdminConfig(secret, lang)
      .then(setConfig)
      .catch((e) => setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Σφάλμα' }))
      .finally(() => setIsLoading(false));
  }, [secret, lang]);

  useEffect(() => { load(); }, [load]);

  const msgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (msgTimerRef.current) clearTimeout(msgTimerRef.current); }, []);
  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    msgTimerRef.current = setTimeout(() => setMessage(null), 3000);
  };

  const save = useCallback(async (updates: Partial<AdminConfig>) => {
    setIsSaving(true);
    try {
      await updateAdminConfig(secret, updates, lang);
      const fresh = await getAdminConfig(secret, lang);
      setConfig(fresh);
      showMsg('success', 'Αποθηκεύτηκε!');
    } catch (e) {
      showMsg('error', e instanceof Error ? e.message : 'Σφάλμα αποθήκευσης');
    } finally {
      setIsSaving(false);
    }
  }, [secret, lang]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Search Configuration</h1>
          <p className="text-muted-foreground">Fine-tune search behavior and synonyms</p>
        </div>
        {message && (
          <div className={`text-sm px-3 py-1.5 rounded-md ${message.type === 'success' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
            {message.text}
          </div>
        )}
      </div>

      <Tabs defaultValue="boosts">
        <TabsList>
          <TabsTrigger value="boosts">Boost Weights</TabsTrigger>
          <TabsTrigger value="synonyms">Synonyms</TabsTrigger>
          <TabsTrigger value="defaults">Defaults</TabsTrigger>
        </TabsList>

        <TabsContent value="boosts" className="space-y-4 mt-4">
          <BoostWeightsTab config={config} onSave={(bw) => save({ boostWeights: bw })} isSaving={isSaving} />
        </TabsContent>

        <TabsContent value="synonyms" className="space-y-4 mt-4">
          <SynonymsTab synonyms={config.synonyms} onSave={(synonyms) => save({ synonyms })} isSaving={isSaving} />
        </TabsContent>

        <TabsContent value="defaults" className="space-y-4 mt-4">
          <DefaultsTab defaults={config.defaults} onSave={(defaults) => save({ defaults })} isSaving={isSaving} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---- Boost Weights Tab ---- */

interface BoostWeightsTabProps {
  config: AdminConfig;
  onSave: (bw: AdminConfig['boostWeights']) => void;
  isSaving: boolean;
}

function BoostWeightsTab({ config, onSave, isSaving }: BoostWeightsTabProps) {
  const [bw, setBw] = useState(config.boostWeights);

  const sliders: Array<{ key: keyof typeof bw; label: string; max: number }> = [
    { key: 'nameBoost', label: 'Name Weight', max: 10 },
    { key: 'descriptionBoost', label: 'Description Weight', max: 5 },
    { key: 'categoryBoost', label: 'Category Weight', max: 5 },
    { key: 'producerBoost', label: 'Producer Weight', max: 5 },
    { key: 'bestSellerBoost', label: 'Best Seller Boost', max: 5 },
    { key: 'promoBoost', label: 'Promo Boost', max: 5 },
    { key: 'inStockBoost', label: 'In-Stock Boost', max: 5 },
  ];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Boost Weights</CardTitle>
          <CardDescription>Field importance scores for search ranking</CardDescription>
        </div>
        <Button size="sm" onClick={() => onSave(bw)} disabled={isSaving}>
          {isSaving ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {sliders.map((s) => (
          <div key={s.key} className="space-y-1.5">
            <div className="flex justify-between">
              <Label>{s.label}</Label>
              <span className="text-sm font-mono text-muted-foreground">{bw[s.key]}</span>
            </div>
            <Slider
              value={[bw[s.key]]}
              onValueChange={([v]) => setBw((prev) => ({ ...prev, [s.key]: v }))}
              min={0}
              max={s.max}
              step={0.5}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ---- Synonyms Tab ---- */

interface SynonymsTabProps {
  synonyms: SynonymGroup[];
  onSave: (synonyms: SynonymGroup[]) => void;
  isSaving: boolean;
}

function SynonymsTab({ synonyms, onSave, isSaving }: SynonymsTabProps) {
  const [groups, setGroups] = useState<SynonymGroup[]>(synonyms);
  const [newTerms, setNewTerms] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const addGroup = () => {
    const terms = newTerms.split(',').map((t) => t.trim()).filter(Boolean);
    if (terms.length < 2) return;
    const group: SynonymGroup = {
      id: `syn_${Date.now()}`,
      terms,
      description: newDesc.trim() || undefined,
    };
    const updated = [...groups, group];
    setGroups(updated);
    onSave(updated);
    setNewTerms('');
    setNewDesc('');
  };

  const deleteGroup = (id: string) => {
    const updated = groups.filter((g) => g.id !== id);
    setGroups(updated);
    onSave(updated);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Synonym Manager</CardTitle>
        <CardDescription>Map alternative terms to canonical search terms</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add new */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <Label className="text-sm font-semibold">Add Synonym Group</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Terms (comma-separated): cab sav, Cabernet Sauvignon, CS"
              value={newTerms}
              onChange={(e) => setNewTerms(e.target.value)}
              className="flex-1"
            />
            <Button size="sm" onClick={addGroup} disabled={isSaving}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Input
            placeholder="Description (optional)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
        </div>

        <Separator />

        {/* Existing groups */}
        {groups.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">Δεν υπάρχουν συνώνυμα</p>
        )}
        <div className="space-y-2">
          {groups.map((g) => (
            <div key={g.id} className="flex items-start gap-3 p-3 rounded-lg border">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1.5 mb-1">
                  {g.terms.map((t) => (
                    <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                  ))}
                </div>
                {g.description && (
                  <p className="text-xs text-muted-foreground">{g.description}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive flex-shrink-0"
                onClick={() => deleteGroup(g.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---- Defaults Tab ---- */

interface DefaultsTabProps {
  defaults: AdminConfig['defaults'];
  onSave: (defaults: AdminConfig['defaults']) => void;
  isSaving: boolean;
}

function DefaultsTab({ defaults, onSave, isSaving }: DefaultsTabProps) {
  const [vals, setVals] = useState(defaults);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Default Results</CardTitle>
          <CardDescription>Configure what appears when the search bar is empty</CardDescription>
        </div>
        <Button size="sm" onClick={() => onSave(vals)} disabled={isSaving}>
          {isSaving ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {([
          { key: 'maxBestSellers', label: 'Max Best Sellers' },
          { key: 'maxPromos', label: 'Max Promos' },
          { key: 'maxPopularSearches', label: 'Max Popular Searches' },
        ] as Array<{ key: keyof typeof vals; label: string }>).map((f) => (
          <div key={f.key} className="flex items-center gap-4">
            <Label className="w-48 flex-shrink-0">{f.label}</Label>
            <Input
              type="number"
              min={0}
              max={20}
              value={vals[f.key]}
              onChange={(e) => setVals((prev) => ({ ...prev, [f.key]: parseInt(e.target.value, 10) || 0 }))}
              className="w-24"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
